import React from "react";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { css } from "@emotion/css";
import { Pane, Text, Loading } from "shared";
import { SPACING, Breakpoint } from "theme";
import { useHealthCareOrganization } from "../queries/useHealthCareOrganizations";
import { useHcoEarnings, EarningsLabelTotal } from "../queries/useHcoEarnings";

const RTM_LABELS = [
  "rtm_first",
  "rtm_adherence",
  "rtm_additional",
  "rtm_short_adherence",
  "rtm_initial",
  "rtm_partial",
] as const;

const LABEL_DISPLAY: Record<string, string> = {
  rtm_initial: "RTM Initial (98975)",
  rtm_first: "RTM First (98980)",
  rtm_additional: "RTM Additional (98981)",
  rtm_adherence: "RTM Adherence (98977)",
  rtm_short_adherence: "RTM Short Adherence (98985)",
  rtm_partial: "RTM Partial (98979)",
};

function formatCents(cents: number): string {
  return `$${(cents / 100).toFixed(2)}`;
}

function getMonthRange(monthsAgo: number): { start: string; end: string } {
  const now = new Date();
  const start = new Date(now.getFullYear(), now.getMonth() - monthsAgo, 1);
  const end = new Date(now.getFullYear(), now.getMonth() - monthsAgo + 1, 1);
  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
}

function countEventsAndLabels(
  earnings: Record<string, Record<string, EarningsLabelTotal>>
): { totalEvents: number; byLabel: Record<string, { count: number; total: number }> } {
  const byLabel: Record<string, { count: number; total: number }> = {};
  let totalEvents = 0;

  for (const periodLabels of Object.values(earnings)) {
    for (const [label, item] of Object.entries(periodLabels)) {
      if (label === "total") continue;
      if (!byLabel[label]) byLabel[label] = { count: 0, total: 0 };
      if (item.total > 0) {
        byLabel[label].total += item.total;
        totalEvents++;
      }
    }
  }

  return { totalEvents, byLabel };
}

const HcoEarningsSection: React.VFC = () => {
  const { t } = useTranslation();
  const { item } = useParams<{ item: string }>();
  const id = Number(item) || 0;
  const { data: hco } = useHealthCareOrganization(id);

  const currentMonth = getMonthRange(0);
  const lastMonth = getMonthRange(1);

  const isPercentage = hco?.platform_fee_model === "percentage";
  const bps = hco?.platform_fee_bps ?? 0;
  const feePercent = bps / 100;

  const { data: currentData, isLoading: currentLoading } = useHcoEarnings(
    id,
    currentMonth.start,
    currentMonth.end,
    isPercentage
  );
  const { data: lastData, isLoading: lastLoading } = useHcoEarnings(
    id,
    lastMonth.start,
    lastMonth.end,
    isPercentage
  );

  if (!hco || !isPercentage) return null;

  const isLoading = currentLoading || lastLoading;

  const currentTotal = currentData?.total?.total ?? 0;
  const lastTotal = lastData?.total?.total ?? 0;
  const currentJa = Math.round(currentTotal * (bps / 10000));
  const lastJa = Math.round(lastTotal * (bps / 10000));

  const currentStatus = currentData?.total;
  const currentBreakdown = currentData ? countEventsAndLabels(currentData.earnings) : null;

  const now = new Date();
  const currentMonthName = now.toLocaleString("en-US", { month: "long", year: "numeric" });
  const lastMonthDate = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const lastMonthName = lastMonthDate.toLocaleString("en-US", { month: "long", year: "numeric" });

  return (
    <>
      <Text paragraph caption secondary>
        {t("form.hco_earnings")}
      </Text>

      <Pane>
        {isLoading ? (
          <Loading />
        ) : (
          <>
            <Pane dark mb={2}>
              <div className={styles.summaryGrid}>
                <div />
                <Text light bold style={{ textAlign: "right" }}>
                  {currentMonthName}
                </Text>
                <Text light bold style={{ textAlign: "right" }}>
                  {lastMonthName}
                </Text>

                <Text light>{t("form.hco_earnings_hco_total")}</Text>
                <Text bold style={{ textAlign: "right" }}>
                  {formatCents(currentTotal)}
                </Text>
                <Text bold style={{ textAlign: "right", opacity: 0.7 }}>
                  {formatCents(lastTotal)}
                </Text>

                <Text light>
                  {t("form.hco_earnings_ja_revenue")} ({feePercent}%)
                </Text>
                <Text bold style={{ textAlign: "right", color: "#2e7d32" }}>
                  {formatCents(currentJa)}
                </Text>
                <Text bold style={{ textAlign: "right", opacity: 0.7 }}>
                  {formatCents(lastJa)}
                </Text>
              </div>
            </Pane>

            {currentBreakdown && Object.keys(currentBreakdown.byLabel).length > 0 && (
              <Pane dark mb={2}>
                <Text light bold mb={1}>
                  {currentMonthName} Breakdown
                </Text>
                <div className={styles.breakdownGrid}>
                  <Text light small>
                    {t("form.hco_earnings_label")}
                  </Text>
                  <Text light small style={{ textAlign: "right" }}>
                    {t("form.hco_earnings_amount")}
                  </Text>
                  <Text light small style={{ textAlign: "right" }}>
                    {t("form.hco_earnings_ja_cut")}
                  </Text>

                  {RTM_LABELS.filter(label => currentBreakdown.byLabel[label]).map(label => {
                    const data = currentBreakdown.byLabel[label];
                    const jaCut = Math.round(data.total * (bps / 10000));
                    return (
                      <React.Fragment key={label}>
                        <Text small nowrap>
                          {LABEL_DISPLAY[label] ?? label}
                        </Text>
                        <Text small style={{ textAlign: "right" }}>
                          {formatCents(data.total)}
                        </Text>
                        <Text small style={{ textAlign: "right", color: "#2e7d32" }}>
                          {formatCents(jaCut)}
                        </Text>
                      </React.Fragment>
                    );
                  })}
                </div>
              </Pane>
            )}

            {currentStatus && (currentStatus.pending > 0 || currentStatus.completed > 0 || currentStatus.discarded > 0) && (
              <Pane dark>
                <Text light small>
                  Status: <Text bold small>{currentStatus.pending > 0 && `${Math.round(currentStatus.pending / 100)} ${t("form.hco_earnings_pending")}`}</Text>
                  {currentStatus.pending > 0 && currentStatus.completed > 0 && " / "}
                  <Text bold small>{currentStatus.completed > 0 && `${Math.round(currentStatus.completed / 100)} ${t("form.hco_earnings_completed")}`}</Text>
                  {(currentStatus.pending > 0 || currentStatus.completed > 0) && currentStatus.discarded > 0 && " / "}
                  <Text bold small>{currentStatus.discarded > 0 && `${Math.round(currentStatus.discarded / 100)} ${t("form.hco_earnings_discarded")}`}</Text>
                </Text>
              </Pane>
            )}
          </>
        )}
      </Pane>
    </>
  );
};

export default HcoEarningsSection;

const styles = {
  summaryGrid: css`
    display: grid;
    grid-template-columns: 1fr auto auto;
    grid-auto-rows: 36px;
    align-items: center;
    grid-column-gap: ${SPACING * 2}px;
    @media screen and (max-width: ${Breakpoint.sm - 1}px) {
      grid-template-columns: 1fr auto auto;
      grid-column-gap: ${SPACING}px;
    }
  `,
  breakdownGrid: css`
    display: grid;
    grid-template-columns: 1fr auto auto;
    grid-auto-rows: 32px;
    align-items: center;
    grid-column-gap: ${SPACING * 2}px;
    margin-top: 8px;
    @media screen and (max-width: ${Breakpoint.sm - 1}px) {
      grid-template-columns: 1fr auto auto;
      grid-column-gap: ${SPACING}px;
    }
  `,
};
